import { sparkToast } from '@spark';
import { TaskType } from '@/shared/domain/taskTypes';
import { detectFileFormat } from '@/features/datasets/utils/filePreviewUtils';
import { formatApiErrorDetail } from '@/shared/utils/formatApiErrorDetail';
import {
    DATASET_SIZE_LIMIT_LABEL,
    MAX_DATASET_FILE_SIZE_BYTES,
    uploadFileTooLargeMessage,
} from '@/shared/constants/datasetSizeLimits';

export function getFileName(rawFileName) {
    return rawFileName?.split(".")[0];
}

export function validateFile(file, options = {}) {
    const {
        selectedTaskType,
        currentCheckedDatasets = [],
        currentCheckedBenchmarks = [],
        isBenchmark = false,
        skipNameLengthCheck = false,
    } = options;

    // Check if task type is selected
    if (selectedTaskType === TaskType.none) {
        return {
            isValid: false,
            error: "Please select a task type before uploading files"
        };
    }

    // Check file name length (can be skipped for initial selection flow)
    if (!skipNameLengthCheck) {
        if (getFileName(file?.name)?.length > 40) {
            return {
                isValid: false,
                error: "File name must be less than 40 characters"
            };
        }
    }

    if (!detectFileFormat(file)) {
        return {
            isValid: false,
            error: "Invalid File Type"
        };
    }

    // Check for dataset/benchmark name mismatch
    const currentCheckedItems = isBenchmark ? currentCheckedBenchmarks : currentCheckedDatasets;
    const existingItemName = getFileName(currentCheckedItems[0]);

    if (existingItemName && existingItemName !== getFileName(file?.name)) {
        return {
            isValid: false,
            error: `${isBenchmark ? "Benchmark" : "Dataset"} name mismatch`
        };
    }

    // Reject oversized files before any upload-url request is issued. A
    // missing size (callers that only pass name/type) stays valid.
    if (Number.isFinite(file?.size) && file.size > MAX_DATASET_FILE_SIZE_BYTES) {
        return {
            isValid: false,
            error: 'File too large',
            detail: uploadFileTooLargeMessage(file.name, file.size),
        };
    }

    return { isValid: true };
}

export function showUploadError(fileName, errorDetail = '') {
    const detail = formatApiErrorDetail(errorDetail);
    sparkToast.error('Upload Failed', {
        content: `${fileName} file upload failed. ${detail}`.trim(),
        detailed: true,
        duration: 6000,
    });
}

const VALIDATION_ERROR_TOASTS = {
    'Please select a task type before uploading files': {
        title: 'Task Type Required',
        content: 'Please select a task type before uploading files.',
    },
    'File name must be less than 40 characters': {
        title: 'File name must be less than 40 characters',
        content: 'Please rename the file and try again.',
    },
    'Invalid File Type': {
        title: 'Invalid File Type',
        content: 'You can only upload CSV, JSON, JSONL, or Parquet files!',
    },
    'Dataset name mismatch': {
        title: 'Dataset name mismatch',
        content: 'Only one dataset is allowed. Your upload must match the existing dataset name.',
    },
    'Benchmark name mismatch': {
        title: 'Benchmark name mismatch',
        content: 'Only one benchmark is allowed. Your upload must match the existing benchmark name.',
    },
    'File too large': {
        title: 'File too large',
        content: `Dataset files must be ${DATASET_SIZE_LIMIT_LABEL} or smaller.`,
    },
};

/**
 * @param {string} error - Validation error key (see VALIDATION_ERROR_TOASTS).
 * @param {string} [detail] - Optional indicative text (e.g. file name + size)
 *   that replaces the generic toast content when provided.
 */
export function showValidationError(error, detail) {
    const toast = VALIDATION_ERROR_TOASTS[error] || { title: 'Upload Error', content: error };
    const content = typeof detail === 'string' && detail.trim() ? detail : toast.content;
    sparkToast.error(toast.title, {
        content,
        detailed: true,
        duration: 6000,
    });
}

export function showNetworkError() {
    sparkToast.error('Upload Failed', {
        content: 'Network error occurred during upload.',
        detailed: true,
        duration: 6000,
    });
}